// Library methods
import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// MUI Components
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import SearchBox from "../SearchBox";
import DefaultModal from "../DefaultModal";
import Alert from "../Alert";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
} from "../../../styles/muiStylesHelper";

// Contexts
import { ExamTransferContext } from "../../../contexts/ExamTransferContext";
import { ReportDataContext } from "../../../contexts/ReportDataContext";

// Utils
import { getLocalDate } from "../../../utils/dateHelper";
import { getTruncatedText } from "../../../utils/textHelper";
import useClinic from "../../../hooks/useClinic";

const ExamTransferModal = ({
  currentPatient = {},
  patientsOptions = [],
  afterTransfer = () => {},
  fromeWhere,
}) => {
  // internationalization
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clinicTimezone } = useClinic();
  const {
    openExamTransferModal,
    transferPatient,
    basePatientId,
    handleExamTransferClose,
    handleExamTransfer,
    setTransferPatient,
    progressionAnalysisFound,
  } = useContext(ExamTransferContext);

  const { exam } = useContext(ReportDataContext);

  // truncated name
  const currentPatientName = useMemo(
    () =>
      getTruncatedText(
        `${currentPatient?.firstName} ${currentPatient?.lastName}`
      ),
    [currentPatient]
  );

  const formattedPatientsOptions = useMemo(
    () =>
      (patientsOptions ?? [])
        .filter((option) => option?.id !== basePatientId)
        .map((option) => {
          const patientName = getTruncatedText(
            `${option?.firstName} ${option?.lastName}`
          );
          const birthdate = getLocalDate(option?.dateOfBirth, clinicTimezone);
          return {
            ...option,
            name: `${patientName} / ${birthdate}`,
          };
        }),
    [patientsOptions, basePatientId, clinicTimezone]
  );

  const shouldDisable = useMemo(() => {
    let isIncluded = false;
    (patientsOptions ?? []).forEach((option) => {
      if (option?.id === transferPatient?.id) isIncluded = true;
    });
    return !isIncluded;
  }, [patientsOptions, transferPatient?.id]);

  const handleOnClickConfirm = useCallback(async () => {
    try {
      await handleExamTransfer();
      if (fromeWhere === `report`) {
        navigate(`/patients/${transferPatient?.id}/exam/${exam?.id}`);
        window.location.href = `/patients/${transferPatient?.id}/exam/${exam?.id}`;
      } else afterTransfer();
    } catch {}
  }, [
    exam?.id,
    handleExamTransfer,
    navigate,
    afterTransfer,
    transferPatient?.id,
    fromeWhere,
  ]);

  return (
    <DefaultModal
      isOpen={openExamTransferModal}
      onClose={handleExamTransferClose}
      title={t("exam_transfer_modal_title")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "20px",
          }}
        >
          <Button
            variant="text"
            sx={ModalCustomCancel}
            onClick={handleExamTransferClose}
          >
            {t("button_cancel")}
          </Button>
          <Button
            variant="outlined"
            sx={ModalCustomConfirm}
            disabled={shouldDisable}
            onClick={handleOnClickConfirm}
          >
            {t("button_confirm")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="20px" gap="20px">
        {progressionAnalysisFound && (
          <Alert
            variant="warning"
            alertContent={t("analysis_status_transfer_check_warning")}
          />
        )}

        <Alert
          variant="warning"
          alertContent={t("exam_transfer_modal_warning")}
        />

        <Box display="flex" flexDirection="column" gap="4px">
          <Typography fontWeight={600}>{t("word_current_patient")}:</Typography>
          <Typography>{`${currentPatientName} / ${getLocalDate(currentPatient?.dateOfBirth, clinicTimezone)}`}</Typography>
        </Box>

        <Typography>{t("select_transfer_patient")}:</Typography>
        {/* Device selector */}
        <SearchBox
          data={formattedPatientsOptions}
          state={transferPatient}
          setState={setTransferPatient}
          label={t("current_patient")}
          propertyBit={4}
          mb={0}
        />
      </Grid>
    </DefaultModal>
  );
};

export default ExamTransferModal;
